import React from "react";
import PropTypes from "prop-types";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import { motion } from "framer-motion";
import linkedinIcon from "../images/icons/linedinIcon.webp";
import { useLanguage } from "../Context/LanguageContext";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const theme = createMuiTheme({
  overrides: {
    MuiTabs: {
      indicator: {
        display: "none",
      },
      root: {
        backgroundColor: "transparent",
        marginBottom: "40px",
      },
      flexContainer: {
        justifyContent: "center",
        flexWrap: "wrap",
      },
    },
    MuiTab: {
      root: {
        fontFamily: "'Inter', sans-serif",
        fontSize: "1.1rem",
        fontWeight: "600",
        color: "var(--text-primary)",
        textTransform: "none",
        borderRadius: "50px",
        margin: "5px",
        minHeight: "45px",
        padding: "0 25px",
        border: "2px solid var(--border-color)",
        background: "var(--bg-secondary)",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "var(--primary-color)",
          color: "var(--primary-color)",
          background: "var(--bg-tertiary)",
        },
        "&.Mui-selected": {
          background: "var(--primary-color)",
          color: "#ffffff",
          borderColor: "var(--primary-color)",
          boxShadow: "0 4px 6px rgba(44, 95, 141, 0.2)",
        },
      },
    },
  },
});

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const countryRepJobTitleTranslations = {
  "Support Engineer": { tr: "Destek Mühendisi", en: "Support Engineer" },
  "Research Associate": { tr: "Araştırma Görevlisi", en: "Research Associate" },
  "Computer Engineer": { tr: "Bilgisayar Mühendisi", en: "Computer Engineer" },
  "Bilgisayar Muhendisi": { tr: "Bilgisayar Mühendisi", en: "Computer Engineer" },
  "Mid Level Full Stack Developer": {
    tr: "Orta Seviye Full Stack Geliştirici",
    en: "Mid Level Full Stack Developer",
  },
  "Business Analyst": { tr: "İş Analisti", en: "Business Analyst" },
  "R&D Director": { tr: "Ar-Ge Direktörü", en: "R&D Director" },
  "Legal Counsel": { tr: "Hukuk Müşaviri", en: "Legal Counsel" },
  "Communication Officer": { tr: "İletişim Sorumlusu", en: "Communication Officer" },
  Biochemist: { tr: "Biyokimyager", en: "Biochemist" },
  "Visual Brand Designer and Corporate Communications Specialist": {
    tr: "Görsel Marka Tasarımcısı ve Kurumsal İletişim Uzmanı",
    en: "Visual Brand Designer and Corporate Communications Specialist",
  },
  "Financial Consultant & Real Estate Advisor": {
    tr: "Finansal Danışman ve Emlak Danışmanı",
    en: "Financial Consultant & Real Estate Advisor",
  },
  "Etch Process Engineer": { tr: "Aşındırma Proses Mühendisi", en: "Etch Process Engineer" },
  "Junior Sales Executive": { tr: "Junior Satış Uzmanı", en: "Junior Sales Executive" },
  "Business Developer": { tr: "İş Geliştirme Uzmanı", en: "Business Developer" },
  "Sales Strategy and Operations, EMEA": {
    tr: "EMEA Satış Stratejisi ve Operasyonları",
    en: "Sales Strategy and Operations, EMEA",
  },
  "AI Data Analyst": { tr: "Yapay Zeka Veri Analisti", en: "AI Data Analyst" },
  "Mechanical Design Engineer": { tr: "Mekanik Tasarım Mühendisi", en: "Mechanical Design Engineer" },
  "Material Scientist": { tr: "Malzeme Bilimcisi", en: "Material Scientist" },
  "Product Engineer": { tr: "Ürün Mühendisi", en: "Product Engineer" },
};

function getLocalizedCountryRepJobTitle(jobTitle, language) {
  const normalizedTitle = (jobTitle || "").trim();
  if (!normalizedTitle) return "";

  const translation = countryRepJobTitleTranslations[normalizedTitle];
  if (!translation) return normalizedTitle;

  return language === "tr" ? translation.tr : translation.en;
}

export default function YonetimKuruluTabs(props) {
  const [value, setValue] = React.useState(0);
  const [flippedCards, setFlippedCards] = React.useState({});
  const { t, language } = useLanguage();

  // Track touch movement to distinguish tap from scroll on mobile
  const touchStartRef = React.useRef({ x: 0, y: 0 });
  const isTouchScrollRef = React.useRef(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const toggleFlip = (key) => {
    setFlippedCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    isTouchScrollRef.current = false;
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    // If finger moved more than 10px, it's a scroll, not a tap
    if (dx > 10 || dy > 10) {
      isTouchScrollRef.current = true;
    }
  };

  const handleCardClick = (cardKey, e) => {
    // If this was a touch scroll, don't flip
    if (isTouchScrollRef.current) {
      return;
    }
    toggleFlip(cardKey);
  };

  const renderTeamMember = (person, folder, index = 0) => {
    const hasPhoto = person.photo && person.photo.trim().length > 0;
    const image = hasPhoto ? `images/${folder}/${person.photo}` : null;
    const desc = (person && person.description) || "";
    const hasDesc = desc.trim().length > 0;
    const isCountryRep = !!person?.city;
    const jobTitle = isCountryRep
      ? getLocalizedCountryRepJobTitle(person.jobTitle, language)
      : person?.jobTitle;

    // Flippable card for members with descriptions (not country reps)
    const canFlip = hasDesc && !isCountryRep;
    const cardKey = `${folder}-${person.name}`;
    const isFlipped = !!flippedCards[cardKey];

    if (canFlip) {
      return (
        <motion.div
          key={person.name}
          className={`card-flip-container${isFlipped ? " flipped" : ""}`}
          style={{ height: "380px" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: index * 0.05 }}
          onClick={(e) => handleCardClick(cardKey, e)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleFlip(cardKey);
            }
          }}
          tabIndex={0}
          role="button"
          aria-expanded={isFlipped}
          aria-label={`${person.name} - ${person.title}. ${isFlipped ? (language === 'tr' ? 'Geri dönmek için tıklayın' : 'Click to go back') : (language === 'tr' ? 'Detayları görmek için tıklayın' : 'Click to read more')}`}
        >
          <div className="card-flip-inner">
            {/* FRONT */}
            <div className="card-flip-front">
              <h4 className="team-role">{person.title}</h4>
              {hasPhoto ? (
                <img alt={person.title} src={image} className="team-photo" loading="lazy" />
              ) : (
                <div className="team-photo team-photo-placeholder">
                  <i className="fa fa-user" aria-hidden="true" style={{ fontSize: '3rem', color: 'var(--text-secondary, #999)' }}></i>
                </div>
              )}
              <h3 className="team-name">
                {person.name}
                {person.linkedin && (
                  <a
                    href={person.linkedin.startsWith('http') ? person.linkedin : `https://${person.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="linkedinWrapper"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={linkedinIcon}
                      alt="linkedin icon"
                      className="linkedinIcon"
                    />
                  </a>
                )}
              </h3>
              <div className="card-flip-hint">
                <i className="fa fa-info-circle" aria-hidden="true"></i>
                {language === "tr" ? "Tıkla ve tanı" : "Click to read more"}
              </div>
            </div>
            {/* BACK */}
            <div className="card-flip-back">
              <div className="card-flip-back-name">{person.name}</div>
              <div className="card-flip-back-role">{person.title}</div>
              <p className="card-flip-back-desc">{desc}</p>
              <div className="card-flip-back-close">
                <i className="fa fa-rotate-left" aria-hidden="true"></i>
                {language === "tr" ? "Geri dön" : "Go back"}
              </div>
            </div>
          </div>
        </motion.div>
      );
    }

    // Non-flippable card (no description or country reps)
    let teamClass;
    if (isCountryRep) {
      teamClass = "team-member";
    } else {
      teamClass = "short-team-member";
    }

    return (
      <motion.div
        key={person.name}
        className={teamClass}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
      >
        <h4 className="team-role">{person.title}</h4>
        {hasPhoto ? (
          <img alt={person.title} src={image} className="team-photo" loading="lazy" />
        ) : (
          <div className="team-photo team-photo-placeholder">
            <i className="fa fa-user" aria-hidden="true" style={{ fontSize: '3rem', color: 'var(--text-secondary, #999)' }}></i>
          </div>
        )}
        <h3 className="team-name">
          {person.name}
          {person.linkedin && (
            <a
              href={person.linkedin.startsWith('http') ? person.linkedin : `https://${person.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="linkedinWrapper"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={linkedinIcon}
                alt="linkedin icon"
                className="linkedinIcon"
              />
            </a>
          )}
        </h3>
        {isCountryRep && (
          <div>
            {jobTitle}
            <p
              className="team-desc"
              style={{ marginTop: "10px", fontStyle: "italic" }}
            >
              {person.city}
            </p>
          </div>
        )}
      </motion.div>
    );
  };

  let yonetimKurulu = [],
    disiplinKurulu = [],
    denetlemeKurulu = [],
    ulkeTemsilcileri = [];

  if (props.data) {
    yonetimKurulu = props.data.yonetimKurulu.map((p, i) =>
      renderTeamMember(p, "yonetimkurulu", i)
    );
    disiplinKurulu = props.data.disiplinKurulu.map((p, i) =>
      renderTeamMember(p, "disiplinkurulu", i)
    );
    denetlemeKurulu = props.data.denetlemeKurulu.map((p, i) =>
      renderTeamMember(p, "denetlemekurulu", i)
    );

    ulkeTemsilcileri = (props.data.ulkeTemsilcileri || [])
      .slice()
      .sort((a, b) => {
        const ka = Object.keys(a)[0] || "";
        const kb = Object.keys(b)[0] || "";
        return ka.localeCompare(kb, "tr", { sensitivity: "base" });
      })
      .map((group, i) => {
        const countryName = Object.keys(group)[0];
        const people = (group[countryName] || []).slice();

        // sort people by name (locale-aware)
        people.sort((p1, p2) => {
          const n1 = p1.name || "";
          const n2 = p2.name || "";
          return n1.localeCompare(n2, "tr", { sensitivity: "base" });
        });

        return (
          <div key={countryName + "-" + i} className="country-group">
            <h3
              style={{
                textAlign: "center",
                margin: "40px 0 20px",
                color: "var(--primary-color)",
              }}
            >
              {countryName}
            </h3>
            <div className="team-grid">
              {people.map((p, idx) => renderTeamMember(p, "ulkeTemsilcileri", idx))}
            </div>
          </div>
        );
      });
  }

  return (
    <section id="yonetimKurulu">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
        >
          <h2>{t("team.title")}</h2>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
        <MuiThemeProvider theme={theme}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            centered
          >
            <Tab label={t("team.board")} {...a11yProps(0)} />
            <Tab label={t("team.discipline")} {...a11yProps(1)} />
            <Tab label={t("team.supervisory")} {...a11yProps(2)} />
            {/* <Tab label={t('team.career')} {...a11yProps(3)} /> */}
            <Tab label={t("team.country")} {...a11yProps(3)} />
          </Tabs>

          <TabPanel value={value} index={0}>
            <div className="team-grid">{yonetimKurulu}</div>
          </TabPanel>
          <TabPanel value={value} index={1}>
            <div className="team-grid">
              {disiplinKurulu}
            </div>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <div className="team-grid">
              {denetlemeKurulu}
            </div>
          </TabPanel>
          <TabPanel value={value} index={3}>
            {ulkeTemsilcileri}
          </TabPanel>
        </MuiThemeProvider>
        </motion.div>
      </div>
    </section>
  );
}
